import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import BookingLayout from 'pages/booking/BookingLayout';
import { useApi } from 'api/api-context';
import { BOOKING_DETAIL_PATH } from 'routes/paths';
import useLang from 'lang';
import 'pages/booking/BookingHome.css';

/**
 * Formats the space operating hours and available weekdays into a single label.
 */
function formatSpaceLocation(space: any, _t: (s: string) => string) {
  const DAYS_SHORT: Record<number, string> = {
    1: _t('Monday'),
    2: _t('Tuesday'),
    3: _t('Wednesday'),
    4: _t('Thursday'),
    5: _t('Friday'),
    6: _t('Saturday'),
    7: _t('Sunday'),
  };

  try {
    const days: number[] = JSON.parse(space.availableDays || '[]').sort((a: number, b: number) => a - b);
    let daysStr: string;
    if (days.length === 7) daysStr = _t('Daily');
    else if (days.length > 1) daysStr = `${DAYS_SHORT[days[0]]} – ${DAYS_SHORT[days[days.length - 1]]}`;
    else if (days.length === 1) daysStr = DAYS_SHORT[days[0]];
    else daysStr = '';
    return `${daysStr} · ${space.availableFrom} – ${space.availableTo}`;
  } catch {
    return `${space.availableFrom} – ${space.availableTo}`;
  }
}

/**
 * Lists all reservable spaces for the current building.
 */
export default function BookingHome() {
  const navigate = useNavigate();
  const { getAction, buildingId } = useApi();
  const { _t } = useLang();
  const [spaces, setSpaces] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const query = buildingId ? { buildingId } : {};
    getAction('GetReservableSpaces')({ query })
      .then((data: any) => {
        if (!cancelled) {
          setSpaces(data.spaces ?? []);
          setLoading(false);
        }
      })
      .catch((err: any) => {
        if (!cancelled) {
          setError(err.message);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <BookingLayout>
      <div className="home">
        <div className="home-inner">
          <div className="home-heading">
            <h1 className="home-title">{_t('What do you want to book today?')}</h1>
            <p className="home-subtitle">{_t('Choose from the available services in the building.')}</p>
          </div>

          {loading && <p className="home-status">{_t('Loading...')}</p>}
          {error && (
            <p className="home-status home-status--error">
              {_t('Error loading spaces:')} {error}
            </p>
          )}

          {!loading && !error && (
            <div className="services-grid">
              {spaces.map((s) => (
                <div key={s.id} className="service-card">
                  <div className="service-card-img-wrap">
                    {s.imageUrl ? (
                      <img src={s.imageUrl} alt={s.name} className="service-card-img" />
                    ) : (
                      <div className="service-card-img service-card-img--placeholder" />
                    )}
                  </div>
                  <div className="service-card-body">
                    <h2 className="service-card-title">{s.name}</h2>
                    <p className="service-card-location">{formatSpaceLocation(s, _t)}</p>
                    <div className="service-card-footer">
                      <div />
                      <button
                        className="service-card-btn"
                        onClick={() => navigate(BOOKING_DETAIL_PATH.insert({ slug: s.slug }))}
                      >
                        {_t('Check availability')}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </BookingLayout>
  );
}
